import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
  ResponsiveContainer,
  Tooltip,
  Customized,
} from "recharts";
import { Winner, SheetDataPoint } from "../../types";
import CustomTooltip from "./CustomTooltip";

type ChunkData = Record<string, string | number>;

function Graph({
  sheetData,
  winners,
}: {
  sheetData: SheetDataPoint[];
  winners: Winner[];
}) {
  const [graphData, setGraphData] = useState<ChunkData[]>([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [monthTicks, setMonthTicks] = useState<Record<string, string>>({});

  useEffect(() => {
    prepareGraphData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [winners]);

  useEffect(() => {
    if (!graphData.length) return;
    setStartDate(graphData[0].name as string);
    setEndDate(graphData[graphData.length - 1].name as string);
    setMonthTicks(prepareMonthTicks(graphData));
  }, [graphData]);

  function prepareGraphData() {
    let preparedData: ChunkData[] = [];

    const chunkSize = 10;
    let chunkItemCounter = 1;
    let chunkData: ChunkData = {};

    sheetData.forEach((row) => {
      chunkData[row["winner"]] = Number(row["Probability"]);

      if (chunkItemCounter < chunkSize) {
        chunkItemCounter++;
        return;
      }

      // set probability to 0 for winners in selected date scope, that weren't in top 10
      winners.forEach((winner) => {
        if (!chunkData[winner.winner]) {
          chunkData[winner.winner] = 0;
        }
      });

      chunkData.name = row["Date"];
      chunkData.month = new Date(row["Date"]).toLocaleString("default", {
        month: "short",
      });
      preparedData.push(chunkData);
      chunkData = {};
      chunkItemCounter = 1;
    });

    setGraphData(preparedData);
  }

  // importing and using it as external component doesn't work
  function GenerateLineShadowFilters() {
    return (
      <>
        {winners.map((winner) => {
          return (
            <filter
              key={winner.winner}
              id={`shadow-${winner.winnerColor?.replace("#", "")}`}
            >
              <feDropShadow
                dx="0"
                dy="0"
                stdDeviation="2"
                floodColor={winner.winnerColor}
              />
              <style>{`
              .recharts-line > path[stroke="${winner.winnerColor}"] {
                filter: url(#shadow-${winner.winnerColor?.replace("#", "")});
              }
              `}</style>
            </filter>
          );
        })}
      </>
    );
  }

  function prepareMonthTicks(graphData: ChunkData[]) {
    const monthTicks: Record<string, string> = {};
    graphData.forEach((dataPoint) => {
      if (
        Object.values(monthTicks)[Object.values(monthTicks).length - 1] ===
        dataPoint.month
      ) {
        return;
      }
      monthTicks[dataPoint.name] = dataPoint.month as string;
    });
    return monthTicks;
  }

  return (
    <div>
      <div className="us-elections-widget__graph-date-scope">
        <p>
          Start date <span>{startDate}</span>
        </p>
        <p>
          End date <span>{endDate}</span>
        </p>
      </div>
      <ResponsiveContainer
        id="us-elections-widget__graph"
        width="100%"
        height={300}
      >
        <LineChart data={graphData} margin={{ top: 10, right: 5, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            interval={0}
            ticks={Object.keys(monthTicks)}
            tick={(props) => {
              props.className =
                "recharts-text recharts-cartesian-axis-tick-value";
              return (
                <text {...props}>
                  <tspan x={props.x} dy="0.71em">
                    {monthTicks[props.payload.value]}
                  </tspan>
                </text>
              );
            }}
          />
          <YAxis domain={[0, "auto"]} unit="%" />
          {winners.map((winner) => {
            return (
              <Line
                key={winner.winner}
                dataKey={winner.winner}
                dot={false}
                stroke={winner.winnerColor}
              />
            );
          })}
          <Tooltip content={<CustomTooltip winners={winners} />} />
          <Legend />
          <Customized component={GenerateLineShadowFilters} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default Graph;
